<!-- @format -->
<template>
    <div class="columns">
        <div class="column">
            <div class="has-text-centered video-container">
                <div ref="playerContainer" class="iframe-wrapper" :style="iframeWrapperStyle">
                    <!-- Vimeo player iframe will be inserted here -->
                </div>
            </div>
            <div v-if="isShowCaption" class="na-post-video-caption-card">
                <span v-if="mediaCaption(video)" class="na-post-video-caption">{{ mediaCaption(video) }}</span>
                <span class="na-post-video-credit">{{ mediaCredit(video) }}</span>
            </div>

            <div class="na-post-video-caption-card">
                <div class="na-post-video-meta">
                    <video-meta :video-player="videoPlayer" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mediaMixin } from '@/mixins/MediaMixin.js';
import { postMixin } from '@/mixins/PostMixin.js';
import { videoMixin } from '@/mixins/VideoMixin.js';
import Player from '@vimeo/player';
import VideoMeta from '../Meta/Index.vue';

export default {
    components: {
        VideoMeta
    },

    mixins: [mediaMixin, postMixin, videoMixin],

    props: {
        // This is the video object, e.g. post.post_media_videos[0]
        video: {
            type: Object,
            required: true
        },
        showVideo: {
            type: Boolean,
            default: false
        },
        autoplay: {
            type: Boolean,
            default: false
        },
        loopVideo: {
            type: Boolean,
            default: false
        },
        isShowCaption: {
            type: Boolean,
            default: false
        },
        allowFullscreen: {
            type: Boolean,
            default: false
        },
        allowPip: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            videoPlayer: null,
            videoWidth: 0,
            videoHeight: 0,
            iframeAspectRatio: '16 / 9',
            iframeMaxWidth: '820px',
            isPlayerReady: false
        };
    },

    watch: {
        video() {
            //this.updateVideoDimensions();
            this.handleShowVideo();
        },
        showVideo() {
            //this.updateVideoDimensions();
            this.handleShowVideo();
        }
    },

    mounted() {
        if (this.showVideo) {
            this.handleShowVideo();
        }
    },

    computed: {
        iframeWrapperStyle() {
            return {
                aspectRatio: this.iframeAspectRatio,
                maxWidth: this.iframeMaxWidth,
                opacity: this.isPlayerReady ? 1 : 0
            };
        }
    },

    methods: {
        /**
         * Destroy video player
         */
        async destroyPlayer() {
            if (!this.videoPlayer) {
                return;
            }
            await this.videoPlayer.unload();
            await this.videoPlayer.destroy();
            this.videoPlayer = null;
        },

        /**
         * Handle showing video or not.  Called when showVideo flag is toggled and upon initial mount.
         */
        handleShowVideo() {
            if (this.showVideo) {
                this.$nextTick(() => {
                    // Make sure DOM is updated first
                    if (this.video) {
                        this.doShowVideo(this.video);
                    }
                });
            } else {
                // Hide video if shown
                this.isPlayerReady = false;
                this.destroyPlayer();
            }
        },

        /**
         * Show video
         */
        async doShowVideo(video) {
            this.isPlayerReady = false;
            await this.prepareVideoLayout(video);

            // Destroy the existing player if it exists
            if (this.videoPlayer) {
                this.videoPlayer.destroy().then(() => {
                    this.videoPlayer = null;
                    this.createPlayer(video);
                });
            } else {
                this.createPlayer(video);
            }
        },
        applyVideoLayout(width, height) {
            if (!width || !height) {
                return;
            }

            this.videoWidth = width;
            this.videoHeight = height;
            this.iframeAspectRatio = `${width} / ${height}`;

            const aspectRatio = width / height;
            const isMobile = window.innerWidth <= 768;

            if (aspectRatio >= 1 && aspectRatio <= 1.2) {
                this.iframeMaxWidth = isMobile ? '100%' : '680px';
            } else if (aspectRatio > 1.2) {
                this.iframeMaxWidth = isMobile ? '100%' : '820px';
            } else {
                this.iframeMaxWidth = isMobile ? '78vw' : '460px';
            }
        },
        async prepareVideoLayout(video) {
            if (!video?.ident) {
                return;
            }

            try {
                const meta = await this.videoGetMeta(video.ident);
                this.applyVideoLayout(meta?.width, meta?.height);
            } catch (error) {
                // Fall back to the default wrapper ratio if meta is unavailable.
            }
        },
        async updateVideoDimensions() {
            if (this.videoPlayer) {
                try {
                    const width = await this.videoPlayer.getVideoWidth();
                    const height = await this.videoPlayer.getVideoHeight();
                    this.applyVideoLayout(width, height);
                } catch (error) {
                    // Ignore sizing updates if Vimeo can't provide dimensions.
                }
            }
        },

        async createPlayer(video) {
            const options = {
                id: video.ident,
                loop: this.loopVideo,
                autoplay: this.autoplay,
                title: false,
                byline: false,
                portrait: false,
                responsive: true,
                pip: this.allowPip,
                fullscreen: this.allowFullscreen,
                //height: '640',
                //width: '320'
            };

            // Create a new player
            this.videoPlayer = new Player(this.$refs.playerContainer, options);
            await this.videoPlayer.ready();

            // Update video dimensions after creating the player
            await this.updateVideoDimensions();
            this.isPlayerReady = true;
        }
    }
};
</script>
